import { backendURL, buildAIAuthOpts } from "../components/backendConfig";
import {
  getStoredActiveAIProvider,
  getStoredAIProviderModelPreference,
} from "./aiProviderConfig";

const AI_PATHS = new Set(["/api/chat", "/api/chatgpt", "/chat", "/api/openai"]);
const API_GATEWAY_STAGE_SEGMENTS = new Set(["$default", "prod", "dev"]);

function rememberAILastRequest(snapshot) {
  if (typeof window === "undefined") return;
  window.__XHANDLE_LAST_AI_REQUEST__ = {
    at: new Date().toISOString(),
    ...snapshot,
  };
}

function getModelFamily(model) {
  const value = String(model || "").trim().toLowerCase();
  if (!value) return null;
  if (value.startsWith("claude")) return "anthropic";
  if (value.startsWith("gemini")) return "gemini";
  return "openai";
}

function normalizeAIPath(pathname) {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (parts.length > 1 && API_GATEWAY_STAGE_SEGMENTS.has(parts[0])) {
    return `/${parts.slice(1).join("/")}`;
  }
  return pathname;
}

function backendPathPrefix(backend) {
  const pathname = String(backend?.pathname || "").replace(/\/+$/g, "");
  return pathname === "/" ? "" : pathname;
}

export function buildBackendAIRequestUrl(aiPath, search = "") {
  const backend = new URL(backendURL, window.location.origin);
  return new URL(`${backendPathPrefix(backend)}${aiPath}${search}`, backend.origin);
}

export function canInjectSelectedModel(body, model, provider, aiPath) {
  if (body?.xhandleModelLocked === true) return false;
  const existingFamily = getModelFamily(body?.model);
  const selectedFamily = getModelFamily(model);
  const activeProvider = String(provider || "").trim().toLowerCase();
  if (!selectedFamily || selectedFamily !== activeProvider) return false;
  if (aiPath === "/api/openai" && selectedFamily !== "openai") return false;
  if (!existingFamily) return true;
  if (body?.xhandleAllowModelOverride !== true) return false;
  return existingFamily === selectedFamily;
}

export function shouldRetryOriginalBody(response) {
  return [400, 404, 502, 503, 504].includes(response?.status);
}

async function logAIResponseFailure(response, context) {
  try {
    const text = await response.clone().text();
    let detail = text;
    try {
      const json = text ? JSON.parse(text) : null;
      detail = json?.error || json?.message || json;
    } catch {}
    const log = context?.willRetry ? console.warn : console.error;
    log("[xHandle AI] request failed", {
      status: response.status,
      statusText: response.statusText,
      url: context?.url,
      injectedModel: context?.injectedModel || null,
      retriedOriginalBody: !!context?.retriedOriginalBody,
      willRetry: !!context?.willRetry,
      detail,
    });
  } catch (error) {
    const log = context?.willRetry ? console.warn : console.error;
    log("[xHandle AI] request failed", {
      status: response.status,
      statusText: response.statusText,
      url: context?.url,
      injectedModel: context?.injectedModel || null,
      retriedOriginalBody: !!context?.retriedOriginalBody,
      willRetry: !!context?.willRetry,
      detail: error?.message || "Unable to read error response",
    });
  }
}

function resolveAIRequest(input) {
  if (typeof window === "undefined") return false;
  const raw = typeof input === "string" || input instanceof URL ? String(input) : input?.url;
  if (!raw) return null;

  try {
    const resolved = new URL(raw, window.location.origin);
    const backend = new URL(backendURL, window.location.origin);
    const sameFrontend = resolved.origin === window.location.origin;
    const sameBackend = resolved.origin === backend.origin;
    const aiPath = normalizeAIPath(resolved.pathname);
    if (!(sameFrontend || sameBackend) || !AI_PATHS.has(aiPath)) return null;

    const target = buildBackendAIRequestUrl(aiPath, resolved.search);

    return { target, aiPath };
  } catch {
    return null;
  }
}

export function installAIRequestAuthShim() {
  if (typeof window === "undefined") return;
  if (window.__XHANDLE_AI_FETCH_PATCH__) return;
  if (typeof window.fetch !== "function") return;

  const nativeFetch = window.fetch.bind(window);
  window.__XHANDLE_AI_FETCH_PATCH__ = true;

  window.fetch = async (input, init = {}) => {
    const resolvedAIRequest = resolveAIRequest(input);
    if (!resolvedAIRequest) {
      return nativeFetch(input, init);
    }

    const authOpts = buildAIAuthOpts();
    const headers = new Headers(init.headers || {});
    Object.entries(authOpts.headers || {}).forEach(([key, value]) => {
      if (value && !headers.has(key)) headers.set(key, value);
    });

    let nextInit = init;
    let injectedModel = false;
    let selectedModel = null;
    if (typeof init.body === "string") {
      try {
        const body = JSON.parse(init.body);
        if (body && typeof body === "object" && !Array.isArray(body)) {
          const provider = getStoredActiveAIProvider();
          const model = getStoredAIProviderModelPreference(provider);
          if (model && canInjectSelectedModel(body, model, provider, resolvedAIRequest.aiPath)) {
            selectedModel = model;
            nextInit = {
              ...init,
              body: JSON.stringify({ ...body, model }),
            };
            injectedModel = true;
          }
        }
      } catch {}
    }

    const target = resolvedAIRequest.target.toString();
    const requestInit = {
      ...nextInit,
      credentials: nextInit.credentials || authOpts.credentials,
      headers,
    };
    rememberAILastRequest({
      phase: "request",
      url: target,
      injectedModel: injectedModel ? selectedModel : null,
      hasAuthorization: headers.has("Authorization"),
      accountId: headers.get("x-account-id") || null,
    });
    let response;
    try {
      response = await nativeFetch(target, requestInit);
    } catch (error) {
      rememberAILastRequest({
        phase: "network-error",
        url: target,
        injectedModel: injectedModel ? selectedModel : null,
        hasAuthorization: headers.has("Authorization"),
        accountId: headers.get("x-account-id") || null,
        detail: error?.message || String(error),
      });
      console.error("[xHandle AI] network error", {
        url: target,
        injectedModel: injectedModel ? selectedModel : null,
        hasAuthorization: headers.has("Authorization"),
        accountId: headers.get("x-account-id") || null,
        detail: error?.message || String(error),
      });
      throw error;
    }

    if (!response.ok && injectedModel && shouldRetryOriginalBody(response) && nextInit.body !== init.body) {
      await logAIResponseFailure(response, {
        url: target,
        injectedModel: selectedModel,
        retriedOriginalBody: false,
        willRetry: true,
      });
      rememberAILastRequest({
        phase: "retry-original-body",
        url: target,
        status: response.status,
        statusText: response.statusText,
        injectedModel: selectedModel,
        hasAuthorization: headers.has("Authorization"),
        accountId: headers.get("x-account-id") || null,
      });
      response = await nativeFetch(target, {
        ...init,
        credentials: init.credentials || authOpts.credentials,
        headers,
      });
      if (!response.ok) {
        await logAIResponseFailure(response, {
          url: target,
          injectedModel: selectedModel,
          retriedOriginalBody: true,
        });
      }
      return response;
    }

    if (!response.ok) {
      rememberAILastRequest({
        phase: "response-error",
        url: target,
        status: response.status,
        statusText: response.statusText,
        injectedModel: injectedModel ? selectedModel : null,
        hasAuthorization: headers.has("Authorization"),
        accountId: headers.get("x-account-id") || null,
      });
      await logAIResponseFailure(response, {
        url: target,
        injectedModel: injectedModel ? selectedModel : null,
        retriedOriginalBody: false,
      });
    } else {
      rememberAILastRequest({
        phase: "response-ok",
        url: target,
        status: response.status,
        injectedModel: injectedModel ? selectedModel : null,
        hasAuthorization: headers.has("Authorization"),
        accountId: headers.get("x-account-id") || null,
      });
    }

    return response;
  };
}
